/**
 * RulebookEditor — the owner's and co-admins' view of the Rulebook tab.
 *
 * Starts from a template (or a past event), then edits one draft: rules are
 * sentence cards grouped by section; tapping one opens RuleEditor. Publishing
 * makes the draft the next live version and can tell registered players.
 * Phones get one column; wide screens get a section list beside the rules.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import RuleSheet from './RuleSheet';
import RuleEditor from './RuleEditor';
import TemplatePicker from './TemplatePicker';
import RulesView, { ChangeBadge } from './RulesView';
import ReadingSheet from './ReadingSheet';
import { IdeasList } from './IdeasList';
import SelectionCard from './SelectionCard';
import { TypeOrSpeak, ImportRules } from './WriteRules';
import { rulebookApi, adjustmentsApi, readApi, errorText } from './api';
import { axiosInstance } from '../../config/axios';
import { KIND_META, SECTION_ACCENT } from './ruleText';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');

// ── Start a rulebook ────────────────────────────────────────────────────

function Setup({ event, onCreated }) {
  const [templateKey, setTemplateKey] = useState('box_5_over');
  const [sources, setSources] = useState([]);
  const [copyFrom, setCopyFrom] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { rulebookApi.copySources(event.id).then(setSources).catch(() => setSources([])); }, [event.id]);

  const start = async () => {
    setBusy(true);
    try {
      const body = copyFrom ? { copy_from_event_id: copyFrom } : { template_key: templateKey || 'blank' };
      onCreated(await rulebookApi.create(event.id, body));
      toast.success('Rulebook started. Check the rules, then publish.');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 lg:max-w-3xl" data-testid="rulebook-setup">
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <p className="font-extrabold text-lg">Set up the rulebook</p>
        <p className="text-sm text-gray-500">Pick the format closest to yours. You can change any rule afterwards.</p>
      </div>
      {sources.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs font-bold text-gray-500 mb-1.5">Copy from one of your events</p>
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
            className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" data-testid="rulebook-copy-from">
            <option value="">Don't copy — use a format below</option>
            {sources.map((s) => <option key={s.event_id} value={s.event_id}>{s.title}</option>)}
          </select>
        </div>
      )}
      {!copyFrom && <TemplatePicker value={templateKey} onChange={setTemplateKey} />}
      <button onClick={start} disabled={busy}
        className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-50" data-testid="rulebook-start">
        {busy ? 'Starting…' : 'Start the rulebook'}
      </button>
    </div>
  );
}

// ── Add a rule ──────────────────────────────────────────────────────────

function AddRuleSheet({ open, eventId, catalogue, onClose, onPick, onReading }) {
  const [tab, setTab] = useState('ideas');
  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setTab(key)} data-testid={`add-tab-${key}`}
      className={`flex-1 h-9 rounded-full text-sm font-bold ${tab === key ? 'bg-[#2B3A67] text-white' : 'text-[#2B3A67]'}`}>{label}</button>
  );
  return (
    <RuleSheet open={open} onClose={onClose} title="Add a rule" testId="add-rule-sheet">
      <div className="flex gap-1 p-1 rounded-full bg-[#E9EBF1] mb-3">
        {tabBtn('ideas', 'Ideas')}{tabBtn('write', 'Type or speak')}{tabBtn('import', 'Import')}
      </div>
      {tab === 'ideas' && <IdeasList catalogue={catalogue} onPick={onPick} />}
      {tab === 'write' && (
        <TypeOrSpeak eventId={eventId} onStarted={onReading}
          onPlainText={() => onPick({ block: 'text', params: {}, source_text: '' })} />
      )}
      {tab === 'import' && <ImportRules eventId={eventId} onStarted={onReading} />}
    </RuleSheet>
  );
}

// ── Publish ─────────────────────────────────────────────────────────────

// Rules that decide the league table (a change can be re-applied to finished matches)
const STANDINGS_BLOCKS = ['match_points', 'walkover', 'tie_breakers', 'knockout_format', 'super_over', 'bowl_out'];

/** The owner's before/after table for applying a change to finished matches. */
function ReapplyPreview({ preview }) {
  if (!preview) return <div className="h-24 rounded-xl bg-gray-100 animate-pulse mt-2" />;
  if (!preview.changed) return <p className="text-xs text-gray-500 mt-2">No team's points or place would change.</p>;
  return (
    <table className="w-full mt-2 text-xs" data-testid="reapply-preview">
      <thead className="text-gray-500">
        <tr><th className="text-left font-semibold py-1">Team</th><th className="font-semibold">Points</th><th className="font-semibold">Place</th></tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {preview.rows.map((r) => {
          const moved = r.points_before !== r.points_after || r.position_before !== r.position_after;
          return (
            <tr key={r.team_id} className={moved ? 'text-[#2B3A67] font-bold' : 'text-gray-500'}>
              <td className="py-1.5 pr-2">{r.group_name ? `${r.group_name} · ` : ''}{r.team_name}</td>
              <td className="text-center whitespace-nowrap">{r.points_before ?? 0} → {r.points_after}</td>
              <td className="text-center whitespace-nowrap">{r.position_before ?? '–'} → {r.position_after}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PublishSheet({ open, data, onClose, onPublished, eventId }) {
  const draft = data.draft;
  const nextNumber = (data.live?.number || 0) + 1;
  const [fromMatch, setFromMatch] = useState(null);
  const [summary, setSummary] = useState('');
  const [notify, setNotify] = useState(true);
  const [reapply, setReapply] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!reapply) return;
    setPreview(null);
    rulebookApi.publishPreview(eventId).then(setPreview).catch((e) => { setReapply(false); setError(errorText(e)); });
  }, [reapply, eventId]);
  if (!open || !draft) return null;
  const counts = { added: 0, changed: 0 };
  draft.rules.forEach((r) => { if (r.changed) counts[r.changed] += 1; });
  const removed = (draft.removed || []).length;
  const firstOpen = data.first_unstarted_match;
  const standingsChanged = !!data.live && data.has_finished_matches && (
    draft.rules.some((r) => r.changed && STANDINGS_BLOCKS.includes(r.block))
    || (draft.removed || []).some((r) => STANDINGS_BLOCKS.includes(r.block)));

  const publish = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await rulebookApi.publish(eventId, {
        applies_from_match: fromMatch, change_summary: summary.trim() || null, notify,
        reapply_finished: standingsChanged && data.is_owner && reapply,
      });
      toast.success(`Version ${out.published_version} is live${out.notified ? ` · ${out.notified} player${out.notified > 1 ? 's' : ''} told` : ''}`);
      onPublished(out);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <RuleSheet open={open} onClose={onClose} title={`Publish version ${nextNumber}`} testId="publish-sheet"
      footer={(
        <button onClick={publish} disabled={busy} className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-50" data-testid="publish-confirm">
          {busy ? 'Publishing…' : `Publish version ${nextNumber}`}
        </button>
      )}>
      <div className="flex gap-2 flex-wrap text-xs font-bold">
        {!data.live && <span className="px-2 py-1 rounded-lg bg-[#E6F5F4] text-[#1F7A75]">{draft.rules.length} rules</span>}
        {data.live && counts.added > 0 && <span className="px-2 py-1 rounded-lg bg-[#E6F5F4] text-[#1F7A75]">{counts.added} new</span>}
        {counts.changed > 0 && <span className="px-2 py-1 rounded-lg bg-[#FCE8EC] text-[#B3263F]">{counts.changed} changed</span>}
        {removed > 0 && <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-600">{removed} removed</span>}
      </div>
      {data.live && firstOpen && (
        <div className="mt-4">
          <p className="text-xs font-bold text-gray-500 mb-1.5">Apply from</p>
          <div className="flex flex-wrap gap-2 items-center">
            <button className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${fromMatch === null ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200'}`}
              onClick={() => setFromMatch(null)}>Every match not started (from match {firstOpen})</button>
            <button className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${fromMatch !== null ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200'}`}
              onClick={() => setFromMatch(firstOpen + 1)}>A later match</button>
            {fromMatch !== null && (
              <input inputMode="numeric" value={fromMatch} onChange={(e) => setFromMatch(Number(e.target.value) || firstOpen + 1)}
                className="w-20 h-10 text-center rounded-xl border border-gray-200 font-bold" aria-label="Match number" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Matches already started keep the rules they began with.</p>
        </div>
      )}
      {standingsChanged && (
        <div className="mt-4 rounded-xl border border-gray-200 p-3" data-testid="reapply-box">
          <p className="text-sm font-bold text-[#2B3A67]">Points or tie-breakers changed</p>
          {data.is_owner ? (
            <>
              <label className="mt-2 flex items-start gap-3 text-sm">
                <input type="checkbox" checked={reapply} onChange={(e) => setReapply(e.target.checked)}
                  className="w-5 h-5 mt-0.5 accent-[#2B3A67]" data-testid="reapply-toggle" />
                <span>Also apply to finished matches <span className="block text-xs text-gray-500">Otherwise they keep the points they were played for.</span></span>
              </label>
              {reapply && <ReapplyPreview preview={preview} />}
            </>
          ) : (
            <p className="text-xs text-gray-500 mt-1">Finished matches keep the points they were played for. Only the event's owner can apply a change to them.</p>
          )}
        </div>
      )}
      <div className="mt-4">
        <p className="text-xs font-bold text-gray-500 mb-1.5">What changed <span className="font-normal">· optional, shown to players</span></p>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={300}
          placeholder={data.live ? 'e.g. Power ball moves to the 3rd ball' : 'e.g. Rules for the 2026 season'}
          className="w-full min-h-[70px] p-3 rounded-xl border border-gray-200 text-sm" />
      </div>
      <label className="mt-3 flex items-center gap-3 text-sm font-semibold">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-5 h-5 accent-[#2B3A67]" />
        Tell registered players
      </label>
      {error && <p className="mt-3 text-sm text-[#B3263F] bg-[#FCE8EC] rounded-lg px-3 py-2" data-testid="publish-error">{error}</p>}
    </RuleSheet>
  );
}

// ── Points adjustments outside a match ─────────────────────────────────

function PointsAdjustments({ event }) {
  const [teams, setTeams] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [amount, setAmount] = useState(-2);
  const [reason, setReason] = useState('');

  useEffect(() => {
    axiosInstance.get(`/v1/events/${event.id}/team-setup/teams`).then((r) => setTeams(r.data || [])).catch(() => setTeams([]));
    adjustmentsApi.list(event.id).then((all) => setList(all.filter((a) => !a.fixture_id))).catch(() => setList([]));
  }, [event.id]);
  if (!teams.length) return null;

  const add = async () => {
    if (!teamId || !reason.trim() || !amount) return toast.error('Choose a team, the points and a reason');
    try {
      const a = await adjustmentsApi.add(event.id, { team_id: teamId, kind: 'points', amount, reason: reason.trim() });
      setList((l) => [...l, a]);
      setReason('');
      toast.success('Points table updated');
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const undo = async (a) => {
    try {
      await adjustmentsApi.remove(event.id, a.id);
      setList((l) => l.filter((x) => x.id !== a.id));
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="points-adjustments">
      <button className="w-full flex justify-between items-center font-bold text-sm" onClick={() => setOpen((v) => !v)}>
        <span><i className="ph-bold ph-ranking" /> Points adjustments {list.length > 0 && <span className="text-gray-400">({list.length})</span>}</span>
        <i className={`ph-bold ${open ? 'ph-caret-up' : 'ph-caret-down'}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">For penalties outside a match, like a player missing the minimum matches. Match penalties are added by the scorer.</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm">
              <option value="">Choose a team</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.team_name}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <button className="w-11 h-11 rounded-xl bg-[#E9EBF1] font-bold" onClick={() => setAmount((a) => (a - 1 === 0 ? -1 : a - 1))}>−</button>
              <span className={`w-14 text-center font-extrabold ${amount < 0 ? 'text-[#B3263F]' : 'text-[#1F7A75]'}`}>{amount > 0 ? '+' : ''}{amount}</span>
              <button className="w-11 h-11 rounded-xl bg-[#E9EBF1] font-bold" onClick={() => setAmount((a) => (a + 1 === 0 ? 1 : a + 1))}>+</button>
            </div>
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Reason, e.g. a player missed 3 matches"
            className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm" />
          <button onClick={add} className="w-full h-11 rounded-xl bg-[#2B3A67] text-white font-bold text-sm">Apply to the points table</button>
          {list.length > 0 && (
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
              {list.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className={`font-extrabold ${a.amount < 0 ? 'text-[#B3263F]' : 'text-[#1F7A75]'}`}>{a.amount > 0 ? '+' : ''}{a.amount} pts</span>
                  <span className="flex-1 min-w-0 truncate">{a.team_name} · {a.reason}</span>
                  <button onClick={() => undo(a)} className="text-xs font-semibold text-gray-500 underline">Undo</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── The editor ──────────────────────────────────────────────────────────

export default function RulebookEditor({ event, data, catalogue, onChange }) {
  const [editing, setEditing] = useState(null);      // rule being edited (or a new one)
  const [adding, setAdding] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [readingId, setReadingId] = useState(null);   // a reading of the organiser's own words
  const [unfinished, setUnfinished] = useState(null); // the latest reading with cards still to check

  const canRead = !!data?.exists && !!data?.can_manage;
  useEffect(() => {
    if (!canRead || readingId) return;
    readApi.recent(event.id)
      .then((rows) => setUnfinished(rows.find((r) => r.pending > 0 && r.status !== 'reading') || null))
      .catch(() => setUnfinished(null));
  }, [event.id, canRead, readingId]);

  const draft = data?.draft;
  const working = useMemo(() => (draft ? draft.rules : (data?.rules || [])), [draft, data]);
  const sections = useMemo(() => (catalogue?.sections || []).map((s) => ({
    ...s, rules: working.filter((r) => r.section === s.key),
    removed: (draft?.removed || []).filter((r) => r.section === s.key),
  })), [catalogue, working, draft]);
  const toFinish = working.filter((r) => r.missing?.length).length;

  if (!data?.exists) return <Setup event={event} onCreated={onChange} />;
  if (previewing) {
    const previewData = draft
      ? { ...data, rules: draft.rules.map((r) => ({ ...r, changed: null })), live: { ...(data.live || {}), number: (data.live?.number || 0) + 1, published_at: new Date().toISOString() } }
      : data;
    return (
      <div className="space-y-3">
        <button onClick={() => setPreviewing(false)} className="h-10 px-4 rounded-full bg-[#2B3A67] text-white text-sm font-bold" data-testid="preview-close">
          <i className="ph-bold ph-arrow-left" /> Back to editing
        </button>
        <RulesView event={event} data={previewData} catalogue={catalogue} preview />
      </div>
    );
  }

  const run = async (fn, okText) => {
    setSaving(true);
    try {
      onChange(await fn());
      if (okText) toast.success(okText);
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const save = async (rule) => {
    const ok = editing?.id
      ? await run(() => rulebookApi.updateRule(event.id, editing.id, rule))
      : await run(() => rulebookApi.addRule(event.id, rule), 'Rule added');
    if (ok) setEditing(null);
  };
  const remove = async () => {
    if (await run(() => rulebookApi.deleteRule(event.id, editing.id), 'Rule removed')) setEditing(null);
  };
  const move = (section, rule, dir) => {
    const ids = section.rules.map((r) => r.id);
    const i = ids.indexOf(rule.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => rulebookApi.reorder(event.id, section.key, ids));
  };
  const discard = () => {
    if (window.confirm('Throw away the unpublished changes?')) run(() => rulebookApi.discardDraft(event.id), 'Changes discarded');
  };
  const canPublish = draft && (draft.has_changes || !data.live);

  return (
    <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 lg:items-start" data-testid="rulebook-editor">
      {/* Section list beside the rules on wide screens */}
      <nav className="hidden lg:block sticky top-40 bg-white rounded-2xl shadow-sm border border-gray-100 p-2">
        {sections.filter((s) => s.rules.length).map((s) => (
          <a key={s.key} href={`#rb-${s.key}`} className="flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold hover:bg-[#F5F7FA]">
            <span className="flex items-center gap-2"><span className="w-1.5 h-4 rounded" style={{ background: SECTION_ACCENT[s.key] }} />{s.label}</span>
            <span className="text-xs text-gray-400">{s.rules.length}</span>
          </a>
        ))}
      </nav>

      <div className="space-y-3">
        {/* Status and main actions */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="rulebook-status">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-extrabold text-lg leading-tight">
                {draft ? `Draft ${data.live ? `v${(data.live.number || 0) + 1}` : ''}`.trim() : `Version ${data.live?.number}`}
              </p>
              <p className="text-xs text-gray-500">
                {data.live
                  ? <><span className="text-[#3AAFA9]">●</span> v{data.live.number} is live · {fmtDate(data.live.published_at)}</>
                  : 'Not published yet — players can’t see it'}
              </p>
            </div>
            {toFinish > 0 && (
              <span className="text-[11px] font-bold bg-[#F4D341] text-[#2B3A67] rounded-lg px-2 py-1 whitespace-nowrap">
                {toFinish} to finish
              </span>
            )}
          </div>
          <button onClick={() => setAdding(true)} data-testid="add-rule"
            className="mt-3 w-full h-11 rounded-full border-2 border-[#3AAFA9] bg-white flex items-center gap-2 px-4 text-left text-gray-400">
            <i className="ph-bold ph-plus-circle text-[#3AAFA9] text-lg" /> Add a rule…
          </button>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setPreviewing(true)} className="flex-1 h-11 rounded-xl border border-[#2B3A67] text-[#2B3A67] font-semibold text-sm" data-testid="preview-players">
              Preview for players
            </button>
            <button onClick={() => setPublishing(true)} disabled={!canPublish || toFinish > 0}
              className="flex-[1.4] h-11 rounded-xl bg-[#2B3A67] text-white font-bold text-sm disabled:opacity-40" data-testid="publish">
              {canPublish ? `Publish v${(data.live?.number || 0) + 1}` : 'Up to date'}
            </button>
          </div>
          {toFinish > 0 && <p className="text-xs text-gray-500 mt-1.5 text-center">Finish the highlighted rules to publish.</p>}
          {unfinished && (
            <button onClick={() => { setReadingId(unfinished.id); setUnfinished(null); }} data-testid="continue-reading"
              className="mt-2 w-full rounded-xl bg-[#FDF5D3] text-[#2B3A67] text-sm font-semibold px-3 py-2 text-left flex items-center gap-2">
              <i className="ph-bold ph-sparkle" />
              <span className="flex-1 min-w-0 truncate">{unfinished.pending} {unfinished.pending === 1 ? 'rule' : 'rules'} from your words still to check</span>
              <i className="ph-bold ph-caret-right" />
            </button>
          )}
          {draft && data.live && draft.has_changes && (
            <button onClick={discard} className="mt-2 w-full text-xs font-semibold text-[#B3263F]" data-testid="discard-draft">Discard unpublished changes</button>
          )}
        </div>

        {/* Sections */}
        {sections.map((s) => {
          const shut = collapsed[s.key];
          if (!s.rules.length && !s.removed.length) return null;
          return (
            <div key={s.key} id={`rb-${s.key}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden scroll-mt-40"
              style={{ borderLeft: `4px solid ${SECTION_ACCENT[s.key]}` }}>
              <button className="w-full flex items-center justify-between px-4 h-12 font-extrabold"
                onClick={() => setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] }))}>
                <span className="flex items-center gap-2"><i className={`ph-bold ${s.icon}`} />{s.label}</span>
                <span className="flex items-center gap-2 text-xs text-gray-400 font-bold">
                  {s.rules.length}<i className={`ph-bold ${shut ? 'ph-caret-down' : 'ph-caret-up'}`} />
                </span>
              </button>
              {!shut && (
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                  {s.rules.map((r, idx) => {
                    const kind = KIND_META[r.kind] || KIND_META.text;
                    return (
                      <li key={r.id} className="flex items-stretch" data-testid="rule-row">
                        <button className="flex-1 text-left px-4 py-3 flex gap-3" onClick={() => setEditing(r)}>
                          <i className={`ph-bold ${kind.icon} mt-0.5`} style={{ color: kind.color }} title={kind.label} />
                          <span className="flex-1 min-w-0">
                            <span className="text-[15px] leading-snug">
                              {r.block === 'text' ? r.source_text : r.segments.filter((g) => g.show || (g.missing && catalogue.blocks[r.block]?.params?.[g.param]?.required)).map((g, i) => (
                                'param' in g
                                  ? <span key={i} className={`mx-0.5 px-1 rounded font-bold ${g.missing ? 'bg-[#FDF5D3] border border-dashed border-[#F4D341]' : 'bg-[#E9EBF1]'}`}>{g.missing ? '?' : g.text}</span>
                                  : <span key={i}>{g.text}</span>
                              ))}
                            </span>
                            <span className="flex flex-wrap gap-1.5 mt-1 items-center">
                              {data.live && <ChangeBadge changed={r.changed} />}
                              {r.stages && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E9EBF1]">{r.stages.map((x) => catalogue.stage_labels[x]).join(' + ')} only</span>}
                              {r.missing?.length > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FDF5D3]">Fill in {r.missing.join(', ').toLowerCase()}</span>}
                            </span>
                          </span>
                        </button>
                        {s.rules.length > 1 && (
                          <div className="flex flex-col justify-center pr-2 text-gray-300">
                            <button aria-label="Move up" disabled={idx === 0} onClick={() => move(s, r, -1)} className="w-8 h-6 disabled:opacity-30 hover:text-[#2B3A67]"><i className="ph-bold ph-caret-up" /></button>
                            <button aria-label="Move down" disabled={idx === s.rules.length - 1} onClick={() => move(s, r, 1)} className="w-8 h-6 disabled:opacity-30 hover:text-[#2B3A67]"><i className="ph-bold ph-caret-down" /></button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                  {s.removed.map((r) => (
                    <li key={r.rule_key} className="px-4 py-3 flex gap-3 text-gray-400">
                      <i className="ph-bold ph-trash mt-0.5" />
                      <span className="flex-1 line-through text-sm">{r.sentence}</span>
                      <ChangeBadge changed="removed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        <PointsAdjustments event={event} />

        {data.live && <SelectionCard event={event} canManage={data.can_manage} />}

        {(data.versions || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <p className="font-bold text-sm mb-2"><i className="ph-bold ph-clock-counter-clockwise" /> History</p>
            <ul className="space-y-1.5">
              {data.versions.map((v) => (
                <li key={v.number} className="flex items-center justify-between text-sm gap-2">
                  <span className="min-w-0 truncate">v{v.number} · {fmtDate(v.published_at)}{v.applies_from_match ? ` · from match ${v.applies_from_match}` : ''}{v.change_summary ? ` — ${v.change_summary}` : ''}</span>
                  <a className="text-[#1F7A75] font-semibold flex-shrink-0" href={rulebookApi.pdfUrl(event.id, v.number)} target="_blank" rel="noopener noreferrer">PDF</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <AddRuleSheet open={adding} eventId={event.id} catalogue={catalogue} onClose={() => setAdding(false)}
        onPick={(rule) => { setAdding(false); setEditing(rule); }}
        onReading={(id) => { setAdding(false); setUnfinished(null); setReadingId(id); }} />
      {readingId && (
        <ReadingSheet open eventId={event.id} importId={readingId} catalogue={catalogue} onRulebook={onChange}
          onClose={() => setReadingId(null)} />
      )}
      {editing && (
        <RuleEditor key={editing.id || `${editing.block}-new`} open catalogue={catalogue} rule={editing}
          onClose={() => setEditing(null)} onSave={save} onDelete={editing.id ? remove : null} saving={saving} />
      )}
      <PublishSheet open={publishing} data={data} eventId={event.id} onClose={() => setPublishing(false)}
        onPublished={(out) => { setPublishing(false); onChange(out); }} />
    </div>
  );
}
